import clsx from "clsx";

import {
  TicketFeatureDecision,
  TicketPriority,
  TicketStatus,
  TicketType,
} from "../../models/Tickets/Ticket";

export const ticketStatusLabels: Record<TicketStatus, string> = {
  [TicketStatus.Open]: "Open",
  [TicketStatus.InProgress]: "In Progress",
  [TicketStatus.Resolved]: "Resolved",
  [TicketStatus.Closed]: "Closed",
};

export const ticketPriorityLabels: Record<TicketPriority, string> = {
  [TicketPriority.Low]: "Low",
  [TicketPriority.Medium]: "Medium",
  [TicketPriority.High]: "High",
  [TicketPriority.Urgent]: "Urgent",
};

export const ticketTypeLabels: Record<TicketType, string> = {
  [TicketType.Bug]: "Bug",
  [TicketType.Feature]: "Feature",
};

export const ticketFeatureDecisionLabels: Record<TicketFeatureDecision, string> = {
  [TicketFeatureDecision.Undecided]: "Undecided",
  [TicketFeatureDecision.Implement]: "Implement",
  [TicketFeatureDecision.Backlog]: "Backlog",
  [TicketFeatureDecision.No]: "No",
};

export const statusClassMap: Record<TicketStatus, string> = {
  [TicketStatus.Open]: "bg-blue-100 text-blue-700",
  [TicketStatus.InProgress]: "bg-yellow-100 text-yellow-700",
  [TicketStatus.Resolved]: "bg-green-100 text-green-700",
  [TicketStatus.Closed]: "bg-gray-200 text-gray-700",
};

export const priorityClassMap: Record<TicketPriority, string> = {
  [TicketPriority.Low]: "bg-slate-100 text-slate-700",
  [TicketPriority.Medium]: "bg-blue-100 text-blue-700",
  [TicketPriority.High]: "bg-orange-100 text-orange-700",
  [TicketPriority.Urgent]: "bg-red-100 text-red-700",
};

export const typeClassMap: Record<TicketType, string> = {
  [TicketType.Bug]: "bg-rose-100 text-rose-700",
  [TicketType.Feature]: "bg-indigo-100 text-indigo-700",
};

export const featureDecisionClassMap: Record<TicketFeatureDecision, string> = {
  [TicketFeatureDecision.Undecided]: "bg-slate-100 text-slate-700",
  [TicketFeatureDecision.Implement]: "bg-emerald-100 text-emerald-700",
  [TicketFeatureDecision.Backlog]: "bg-amber-100 text-amber-700",
  [TicketFeatureDecision.No]: "bg-gray-200 text-gray-600",
};

/** Accent colours for the summary bar tiles, matching the pills above. */
export const statusAccentMap: Record<TicketStatus, string> = {
  [TicketStatus.Open]: "border-l-blue-500",
  [TicketStatus.InProgress]: "border-l-amber-500",
  [TicketStatus.Resolved]: "border-l-green-500",
  [TicketStatus.Closed]: "border-l-gray-400",
};

export const priorityAccentMap: Record<TicketPriority, string> = {
  [TicketPriority.Low]: "border-l-slate-400",
  [TicketPriority.Medium]: "border-l-blue-500",
  [TicketPriority.High]: "border-l-orange-500",
  [TicketPriority.Urgent]: "border-l-red-500",
};

export const ticketStatusOptions = [
  TicketStatus.Open,
  TicketStatus.InProgress,
  TicketStatus.Resolved,
  TicketStatus.Closed,
];

export const ticketPriorityOptions = [
  TicketPriority.Low,
  TicketPriority.Medium,
  TicketPriority.High,
  TicketPriority.Urgent,
];

export const ticketTypeOptions = [TicketType.Bug, TicketType.Feature];

export const ticketFeatureDecisionOptions = [
  TicketFeatureDecision.Undecided,
  TicketFeatureDecision.Implement,
  TicketFeatureDecision.Backlog,
  TicketFeatureDecision.No,
];

const pillBaseClasses =
  "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium";

interface TicketPillProps {
  className?: string;
}

export function TicketStatusPill({
  status,
  className,
}: TicketPillProps & { status: TicketStatus }) {
  return (
    <span className={clsx(pillBaseClasses, statusClassMap[status], className)}>
      {ticketStatusLabels[status]}
    </span>
  );
}

export function TicketPriorityPill({
  priority,
  className,
}: TicketPillProps & { priority: TicketPriority }) {
  return (
    <span
      className={clsx(pillBaseClasses, priorityClassMap[priority], className)}
    >
      {ticketPriorityLabels[priority]}
    </span>
  );
}

export function TicketTypePill({
  type,
  className,
}: TicketPillProps & { type: TicketType }) {
  return (
    <span className={clsx(pillBaseClasses, typeClassMap[type], className)}>
      {ticketTypeLabels[type]}
    </span>
  );
}

/**
 * Internal triage state on a feature request. Renders nothing for bugs, or for a feature
 * nobody has triaged yet, so the list only gains a pill once a decision has been made.
 */
export function TicketFeatureDecisionPill({
  type,
  decision,
  className,
}: TicketPillProps & {
  type: TicketType;
  decision?: TicketFeatureDecision;
}) {
  if (
    type !== TicketType.Feature ||
    decision === undefined ||
    decision === null ||
    decision === TicketFeatureDecision.Undecided
  ) {
    return null;
  }

  return (
    <span
      className={clsx(
        pillBaseClasses,
        featureDecisionClassMap[decision],
        className,
      )}
      title="Internal - not visible to the customer"
    >
      {ticketFeatureDecisionLabels[decision]}
    </span>
  );
}

export { pillBaseClasses };
